import angular from 'angular';
import moment from 'moment';

import { logger } from '../../services/logger';
import { RootScope } from '../../types';
import currencySymbols from './currencySymbols';

export interface ExchangeRates {
  [currencyPair: string]: number;
}

interface EmptyRatesInfo {
  // our backend returns empty array if no currency data yet
  rates: [];
}

interface RatesInfo {
  base: 'USD';
  rates: {
    EUR: number;
    BYN: number;
    KZT: number;
    RUB: number;
    UAH: number;
    VND: number;

    /**
     * This field returned only by our backend
     */
    USD?: number;
  };

  /**
   * Field from openexchange API
   */
  disclaimer?: string;

  /**
   * Field from openexchange API
   */
  license?: string;

  /**
   * Unix timestamp in seconds
   */
  timestamp: number;
}

export default angular
  .module('est.currency', [])
  .value('exchangeRates', {
    // 'USD/UAH': 0.037,
    // 'UAH/USD': 27.08
  } as ExchangeRates)
  .run(run);

run.$inject = ['$rootScope', 'exchangeRates', '$http', 'localStorageService'];

function run(
  $rootScope: RootScope,
  exchangeRates: ExchangeRates,
  $http: angular.IHttpService,
  localStorageService: angular.local.storage.ILocalStorageService,
) {
  $rootScope.getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || '';
  };

  $rootScope.updateCurrencyRates = async () => {
    const lsCurrencyRates: RatesInfo | null =
      localStorageService.get('currencyRates');

    if (
      lsCurrencyRates &&
      moment.unix(lsCurrencyRates.timestamp).add(1, 'days').isAfter(moment()) &&
      lsCurrencyRates.rates['RUB'] &&
      lsCurrencyRates.rates['VND'] &&
      lsCurrencyRates.rates['BYN'] &&
      lsCurrencyRates.rates['KZT']
    ) {
      populateExchangeRates(lsCurrencyRates.rates);

      return;
    }

    try {
      let { data } = await $http.get<RatesInfo | EmptyRatesInfo>(
        '/api/settings/exchange_rates/latest',
        {},
      );

      if (Array.isArray(data.rates) && !data.rates.length) {
        // no rates yet in DB
        ({ data } = await $http.get<RatesInfo>(
          'https://openexchangerates.org/api/latest.json?app_id=182c05a7e7024eb9918a1d291368c9a6&symbols=EUR%2CUAH%2CRUB%2CVND%2CBYN%2CKZT',
          {
            headers: {
              'X-Requested-With': undefined,
            },
          },
        ));
      }

      localStorageService.remove('currencyRates');
      localStorageService.set('currencyRates', data);

      populateExchangeRates((data as RatesInfo).rates);
    } catch (error) {
      logger.error('Error fetching currency rates', {
        error,
      });
    }
  };

  const round = (num: number, precision: number) =>
    Number(Number(num).toFixed(precision));

  function populateExchangeRates(newRates: RatesInfo['rates']) {
    exchangeRates['UAH/USD'] = round(newRates['UAH'], 2);
    exchangeRates['USD/UAH'] = round(1 / Number(newRates['UAH']), 3);

    exchangeRates['RUB/USD'] = round(newRates['RUB'], 2);
    exchangeRates['USD/RUB'] = round(1 / Number(newRates['RUB']), 3);

    exchangeRates['BYN/USD'] = round(newRates['BYN'], 2);
    exchangeRates['USD/BYN'] = round(1 / Number(newRates['BYN']), 3);

    exchangeRates['VND/USD'] = round(newRates['VND'], 2);
    exchangeRates['USD/VND'] = round(1 / Number(newRates['VND']), 3);

    exchangeRates['KZT/USD'] = round(newRates['KZT'], 2);
    exchangeRates['USD/KZT'] = round(1 / Number(newRates['KZT']), 4);
  }
}
